/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1902772238 — localStorage стратегія */

import {
    STORAGE_RENEW_INTERVAL,
    TAB_SYNC_DURATION,
} from '../constants';
import { localStorageService } from '../services/local-storage';
import { sessionStorageService } from '../services/session-storage';
import { Order } from '../types';
import { debug } from './general';

const clearSavedVMState = () => {
    localStorageService.removeItem('VM_STATE');
    localStorageService.removeItem('VM_SYNC_TIMESTAMP');
};

const clearTabData = () => {
    // Only the *killed* tab's per-tab sessionStorage entries are cleared.
    // localStorage.TAB_ID is the ownership marker for the surviving tab —
    // removing it would (a) cascade-fire a TAB_ID storage event with
    // newValue=null that the surviving tab's useTabSync could match against
    // its own tabId and trigger a self-kill, and (b) leave localStorage
    // empty so a future new tab can't detect takeover via storage events.
    sessionStorageService.removeItem('TAB_ID');
    sessionStorageService.removeItem('TAB_START_TIMESTAMP');
};

export const loadSavedVMState = (): Record<string, unknown> & { isSavedDataExpired: boolean } => {
    const storedData = localStorageService.getItem('VM_STATE') as Record<string, unknown>;
    const storedTimestamp = localStorageService.getItem('VM_SYNC_TIMESTAMP');

    debug(' >> storedData', 'data');
    debug(` >> storedTimestamp: ${storedTimestamp}`, 'data');
    debug(
        ` >> expiration counter: ${
            (Date.now() - parseInt(storedTimestamp)) / 1000
        } and allowed: ${TAB_SYNC_DURATION / 1000}`,
        'error',
    );

    const isSavedDataExpired =
        Date.now() - parseInt(storedTimestamp) > TAB_SYNC_DURATION;
    debug(
        ` >> Is stored state expired: ${
            isSavedDataExpired ? 'expired' : 'still valid'
        }`,
        'data',
    );

    return {
        ...storedData,
        isSavedDataExpired,
    };
};

export const loadSavedOrderState = (vmConnectionDelay: number = 0): Record<string, unknown> & { isSavedDataExpired: boolean } => {
    const storedData = localStorageService.getItem('ORDER_STATE') as Record<string, unknown>;
    const storedTimestamp = localStorageService.getItem('ORDER_SYNC_TIMESTAMP');

    debug(' >> stored order', 'data');
    debug(
        ` >> expiration counter: ${
            (Date.now() - parseInt(storedTimestamp)) / 1000
        } and allowed: ${TAB_SYNC_DURATION / 1000 + vmConnectionDelay} `,
        'error',
    );

    const isSavedDataExpired =
        Date.now() - parseInt(storedTimestamp) >
        TAB_SYNC_DURATION + vmConnectionDelay * 1000;
    debug(
        ` >> Is stored order state expired: ${
            isSavedDataExpired ? 'expired' : 'still valid'
        }`,
        'data',
    );

    return {
        ...storedData,
        isSavedDataExpired,
    };
};

export const storeOrderState = (order: Order): void => {
    localStorageService.setItem('ORDER_STATE', order);
    localStorageService.setItem('ORDER_SYNC_TIMESTAMP', Date.now().toString());
};

export const clearSavedOrderState = () => {
    localStorageService.removeItem('ORDER_STATE');
    localStorageService.removeItem('ORDER_SYNC_TIMESTAMP');
};

// --- Timer end-time persistence -------------------------------------------
//
// Stored as JSON `{ endTime, scope }` where `scope` is the URL pathname at
// timer start. useTimer.start() resumes from `endTime` only when the current
// pathname matches `scope`, otherwise starts fresh. Keeps the apparatus-clock
// sync on reload + cross-tab takeover; isolates timers per screen.

export interface SavedTimerEnd {
    endTime: number;
    scope: string;
}

export const loadSavedTimerEnd = (): SavedTimerEnd | null => {
    const raw = localStorageService.getItem('TIMER_END_TIMESTAMP');
    if (!raw || raw === '0') return null;
    try {
        const parsed = JSON.parse(raw) as SavedTimerEnd;
        if (!Number.isFinite(parsed.endTime) || parsed.endTime <= 0) return null;
        if (typeof parsed.scope !== 'string' || parsed.scope.length === 0) return null;
        return parsed;
    } catch {
        return null;
    }
};

export const storeTimerEnd = (endTime: number, scope: string): void => {
    localStorageService.setItem(
        'TIMER_END_TIMESTAMP',
        JSON.stringify({ endTime, scope }),
    );
};

export const clearSavedTimerEnd = (): void => {
    localStorageService.removeItem('TIMER_END_TIMESTAMP');
};

export const clearAllSavedStates = () => {
    debug('clearing all saved states', 'error');
    clearSavedVMState();
    clearSavedOrderState();
    clearTabData();
    // TIMER_END_TIMESTAMP is intentionally NOT cleared here: it's scoped by
    // URL pathname inside useTimer, so a stale value is auto-ignored on a
    // different screen. Preserving it lets cross-tab takeover on the same
    // URL inherit the apparatus countdown instead of restarting.
};

export const refreshLocalStorage = (version: string) => {
    const storedAppVersion = localStorageService.getItem('APP_VERS');
    const storedTimestamp = localStorageService.getItem('STORE_TIMESTAMP');
    const isStorageExpired =
        Date.now() - parseInt(storedTimestamp) > STORAGE_RENEW_INTERVAL;

    debug(
        `[storage] storedAppVersion: ${storedAppVersion} vs version: ${version}`,
        'data',
    );
    if (storedAppVersion !== version || isStorageExpired) {
        debug('[storage] refreshing local storage', 'data');
        localStorageService.clear();
        sessionStorageService.clear();
        localStorageService.setItem('STORE_TIMESTAMP', Date.now().toString());
    }
    localStorageService.setItem('APP_VERS', version);
};
